import { de, type MessageKey } from './de';
import { en } from './en';
import { useSettings } from '@/store/settings';

export type Language = 'de' | 'en';
export type { MessageKey };

const dictionaries: Record<Language, Partial<Record<MessageKey, string>>> = { de, en };

export function translate(lang: Language, key: MessageKey, params?: Record<string, string | number>): string {
  const raw = dictionaries[lang][key] ?? de[key] ?? key;
  if (!params) return raw;
  return raw.replace(/\{(\w+)\}/g, (_, name: string) =>
    params[name] !== undefined ? String(params[name]) : `{${name}}`,
  );
}

/** Hook: returns a translate function bound to the current UI language. */
export function useT(): (key: MessageKey, params?: Record<string, string | number>) => string {
  const lang = useSettings((s) => s.settings.language);
  return (key, params) => translate(lang, key, params);
}

/** Number formatting with Swiss conventions (apostrophe thousands separator). */
export function fmtNumber(n: number, digits = 0): string {
  return new Intl.NumberFormat('de-CH', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(n);
}

export function fmtDuration(totalSec: number): string {
  const s = Math.max(0, Math.round(totalSec));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const mm = String(m).padStart(2, '0');
  const ss = String(sec).padStart(2, '0');
  return h > 0 ? `${h}:${mm}:${ss}` : `${m}:${ss}`;
}
